import React, { useEffect } from 'react';
import { Factory, Settings, Users, PenTool, ShieldCheck, Zap } from 'lucide-react';

const ManufacturingFacilities = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/banner.jpg)` }}
        >
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        <div className="relative z-10 text-center px-4 animate-slide-up">
          <span className="text-secondary font-bold tracking-widest uppercase mb-4 block">Production Capability</span>
          <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6">Manufacturing Facilities</h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto font-light">
            최적화된 생산 환경과 숙련된 기술력으로 최고의 품질을 약속드립니다.
          </p>
        </div>
      </section>

      {/* Intro Stats */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="section-container">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-primary mx-auto mb-4">
                <Factory size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Clean Environment</h3>
              <p className="text-slate-500">
                먼지와 오염을 최소화한<br />쾌적한 제조 환경 유지
              </p>
            </div>
            <div className="p-6 border-l border-r border-slate-100">
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-primary mx-auto mb-4">
                <Settings size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Advanced Equipment</h3>
              <p className="text-slate-500">
                정밀 가공을 위한<br />최신 자동화 설비 구축
              </p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-primary mx-auto mb-4">
                <Users size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Expert Engineers</h3>
              <p className="text-slate-500">
                20년 경력의 베테랑<br />전문 엔지니어 상주
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Harness Facility Section */}
      <section className="py-24 bg-white">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1 relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-50 to-slate-100 rounded-3xl transform -rotate-3 z-0"></div>
              <img
                src={`${process.env.PUBLIC_URL}/images/harness/harness_room.png`}
                alt="Harness Manufacturing Room"
                className="relative z-10 rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="order-1 md:order-2 space-y-6">
              <div className="flex items-center space-x-2 text-primary font-bold tracking-wide uppercase text-sm">
                <span className="w-8 h-[2px] bg-primary"></span>
                <span>Wire Harness Division</span>
              </div>
              <h2 className="text-4xl font-display font-bold text-slate-900 leading-tight">
                정밀함이 생명인<br />하네스 제조 라인
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                하네스팀은 고급 자동화 기기와 정밀 수작업을 병행하여 불량률 0%에 도전합니다.
                다품종 소량 생산부터 대량 생산까지 모든 요구사항에 유연하게 대응합니다.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="bg-slate-50 p-4 rounded-xl">
                  <PenTool className="text-accent mb-2" size={24} />
                  <h4 className="font-bold text-slate-900">Customization</h4>
                  <p className="text-sm text-slate-500 mt-1">고객 맞춤형 설계 제공</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl">
                  <Zap className="text-accent mb-2" size={24} />
                  <h4 className="font-bold text-slate-900">Efficiency</h4>
                  <p className="text-sm text-slate-500 mt-1">고효율 자동화 공정</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Control Box Facility Section */}
      <section className="py-24 bg-slate-50">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="flex items-center space-x-2 text-primary font-bold tracking-wide uppercase text-sm">
                <span className="w-8 h-[2px] bg-primary"></span>
                <span>Control Box Division</span>
              </div>
              <h2 className="text-4xl font-display font-bold text-slate-900 leading-tight">
                체계적이고 안전한<br />제어반 생산 라인
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                C-Box팀은 다양한 산업 규격을 준수하며, 고도로 숙련된 기술자들이 전장 배선을 담당합니다.
                철저한 기능 테스트와 검수를 통해 최고의 신뢰성을 보장합니다.
              </p>
              <ul className="space-y-4 pt-4">
                <li className="flex items-start">
                  <div className="bg-white p-2 rounded-full shadow-sm text-primary mr-4 mt-1">
                    <ShieldCheck size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Safety First</h4>
                    <p className="text-slate-500 text-sm">국제 안전 기준을 준수하는 엄격한 관리</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-white p-2 rounded-full shadow-sm text-primary mr-4 mt-1">
                    <Users size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Skilled Workforce</h4>
                    <p className="text-slate-500 text-sm">전기 기능장급 전문 인력 보유</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-bl from-blue-50 to-slate-200 rounded-3xl transform rotate-3 z-0"></div>
              <img
                src={`${process.env.PUBLIC_URL}/images/c-box/cbox_room.png`}
                alt="Control Box Manufacturing Room"
                className="relative z-10 rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManufacturingFacilities;
